import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { CircleIcon, CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';

const CustomNode = ({ data, type, icon, id }) => {
  // Status styles mapping with default values
  const statusStyles = {
    completed: {
      icon: <CheckCircle2Icon className="w-4 h-4" />,
      className: 'bg-green-100 text-green-700 border-green-300'
    },
    running: {
      icon: <CircleIcon className="w-4 h-4 animate-pulse" />,
      className: 'bg-blue-100 text-blue-700 border-blue-300'
    },
    error: {
      icon: <AlertCircleIcon className="w-4 h-4" />,
      className: 'bg-red-100 text-red-700 border-red-300'
    },
    waiting: {
      icon: <CircleIcon className="w-4 h-4" />,
      className: 'bg-gray-100 text-gray-700 border-gray-300'
    },
    default: {
      icon: <CircleIcon className="w-4 h-4" />,
      className: 'bg-gray-100 text-gray-700 border-gray-300'
    }
  };

  // Get status style with fallback to default
  const getStatusStyle = (status) => {
    if (!status || !statusStyles[status]) {
      return statusStyles.default;
    }
    return statusStyles[status];
  };

  const statusStyle = getStatusStyle(data?.status);

  return (
    <div className="group relative transition-all duration-300">
        {/* Delete button - appears on hover */}
        {data?.onDelete && (
  <button 
    className="absolute top-1 right-1 w-4 h-4 rounded-full 
              flex items-center justify-center
              bg-red-300 text-white hover:bg-red-500
              transition-all duration-300 font-bold z-10 leading-none pt-[0px] -mt-[2px] text-[14px]"
    onClick={(e) => {
      e.stopPropagation(); // Prevent node selection when clicking delete
      data.onDelete(id);
    }}
  >
    ×
  </button>
)}

      {/* Handles with improved visibility */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-blue-400 border-2 border-white transition-all duration-300 hover:w-4 hover:h-4"
      />
      
      {/* Main node container */}
      <div className="px-6 py-4 rounded-xl bg-white border border-gray-200 shadow-lg 
                      hover:shadow-xl transition-all duration-300 hover:scale-105
                      hover:border-blue-200 min-w-[200px]">
        <div className="flex items-center gap-4">
          {/* Icon container */}
          <div className="rounded-lg w-10 h-10 flex items-center justify-center 
                        bg-gradient-to-br from-blue-50 to-indigo-50 
                        text-xl shadow-sm">
            {icon}
          </div>
          
          <div className="flex-1">
            {/* Title */}
            <div className="font-semibold text-gray-800 group-hover:text-blue-600 
                          transition-colors duration-300">
              {data?.label || 'Unnamed Node'}
            </div>
            
            {/* Type label */}
            <div className="text-xs text-gray-500 mt-0.5">{type}</div>
          </div>
        </div>

        {/* Status Badge - only show if status exists */}
        {data?.status && (
          <div className="mt-4 flex items-center">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full 
                            text-xs font-medium border ${statusStyle.className}`}>
              {statusStyle.icon}
              <span className="capitalize">{data.status}</span>
            </span>
          </div>
        )}

        {/* Results display - show key metrics */}
        {data?.results && data.status === 'completed' && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-xs space-y-1">
              {/* For CNN results */}
              {data.results.num_classes && (
                <>
                  <div className="text-gray-600">
                    <span className="font-medium">Accuracy:</span> {(data.results.val_accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="text-gray-600">
                    <span className="font-medium">Classes:</span> {data.results.num_classes}
                  </div>
                </>
              )}
              
              {/* For traditional ML results */}
              {data.results.accuracy && !data.results.num_classes && (
                <div className="text-gray-600">
                  <span className="font-medium">Accuracy:</span> {(data.results.accuracy * 100).toFixed(1)}%
                </div>
              )}
              
              {/* For regression results */}
              {data.results.r2 && (
                <div className="text-gray-600">
                  <span className="font-medium">R² Score:</span> {data.results.r2.toFixed(3)}
                </div>
              )}

              {/* For clustering results */}
              {data.results.n_clusters && (
                <>
                  <div className="text-gray-600">
                    <span className="font-medium">Clusters:</span> {data.results.n_clusters}
                  </div>
                  {data.results.silhouette_score && (
                    <div className="text-gray-600">
                      <span className="font-medium">Silhouette:</span> {data.results.silhouette_score.toFixed(3)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 !bg-blue-400 border-2 border-white transition-all duration-300 hover:w-4 hover:h-4"
      />
    </div>
  );
};

// Node-specific components with custom icons
export const UploadDatasetNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Upload Dataset" icon="📁" id={id} />
));

export const ConfigureFeaturesNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Configure Features" icon="⚙️" id={id} />
));

export const ImputeNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Impute Missing Values" icon="🔄" id={id} />
));

export const ScaleNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Scale Features" icon="📊" id={id} />
));

export const EncodeNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Encode Categories" icon="🏷️" id={id} />
));

export const SelectFeaturesNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Feature Selection" icon="🎯" id={id} />
));

export const ReduceDimensionsNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Dimensionality Reduction" icon="📉" id={id} />
));

export const TrainModelNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Train Model" icon="🤖" id={id} />
));

// ADD THE NEW CNN NODE
export const TrainCNNNode = memo(({ data, id }) => (
  <CustomNode data={data} type="Train CNN" icon="🧠" id={id} />
));

// Node types mapping
export const nodeTypes = {
  uploadDataset: UploadDatasetNode,
  configureFeatures: ConfigureFeaturesNode,
  impute: ImputeNode,
  scale: ScaleNode,
  encode: EncodeNode,
  selectFeatures: SelectFeaturesNode,
  reduceDimensions: ReduceDimensionsNode,
  trainModel: TrainModelNode,
  trainCNN: TrainCNNNode  // ADD THIS
};